import { useMemo, useState, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

import useAppStore from "../../store/useAppStore"
import useLayoutStore from "../../store/useLayoutStore";

import ViewPresenceHeader from "../elements/ViewPresenceHeader"
import EmployeeCard from "../elements/EmployeeCard";
import LoadingSpinner from "../elements/LoadingSpinner";

const NO_GROUP_LABEL = "Bez grupe";

const normalizeGroupName = (name) => {
    if (name == null || name === "" || name === "null" || name === "undefined") return NO_GROUP_LABEL;
    return name;
};

const PresenceView = () => {
    const loading = useAppStore((state) => state.loading);
    const employeesData = useAppStore((state) => state.employeesData);

    const employeesByGroup = useMemo(() => {
        if (loading || !employeesData) return {};

        const grouped = {};

        Object.values(employeesData).forEach((emp) => {
            const groupKey = normalizeGroupName(emp.group);
            if (!grouped[groupKey]) grouped[groupKey] = [];
            grouped[groupKey].push(emp);
        });

        return grouped;
    }, [loading, employeesData]);

    return (
        <div className="view-container">
            <ViewPresenceHeader />
            <AnimatePresence mode="wait">
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <motion.div
                        key="content"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="view-groups-card-container"
                    >
                        {Object.entries(employeesByGroup)
                            .sort(([groupA], [groupB]) => {
                                if (groupA === NO_GROUP_LABEL) return 1;
                                if (groupB === NO_GROUP_LABEL) return -1;
                                return groupA.localeCompare(groupB, "sr", { sensitivity: "base" });
                            })
                            .map(([groupName, groupEmployees]) => (
                                <EmployeeGroupSection key={groupName} groupName={groupName} employees={groupEmployees} />
                            ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default PresenceView

const EMPTY_GROUP_STATE = { allCards: 0, visibleCards: 0, currentActiveCards: 0 }

const EmployeeGroupSection = ({ groupName, employees }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    const cardsInRow = useLayoutStore(state => state.employeesCardsInRow)
    const groupState = useAppStore(state => state.groupsState[groupName]) ?? EMPTY_GROUP_STATE

    const presenceVisibility = useAppStore(state => state.presenceVisibility)

    const [isVisible, setIsVisible] = useState(groupState.visibleCards > 0)

    const emptySlots = Math.max(0, cardsInRow - groupState.visibleCards)

    const smallScreen = useLayoutStore(state => state.smallScreen)

    useLayoutEffect(() => {
        if (groupState.visibleCards === 0) {
            setIsVisible(false)
        } else {
            setIsVisible(true)
        }
    }, [groupState.visibleCards, presenceVisibility])

    return (
        <motion.div
            className={`view-group-section  pv-group-section ${isDarkTheme ? 'dark-group-section' : 'white-group-section'}`}
            animate={{ height: isVisible ? "auto" : 49 }}
            initial={false}
            transition={{ duration: 0.28, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
        >
            <div
                className={`view-group-name ${isDarkTheme ? 'dark-group-name' : 'white-group-name'}`}
                onClick={() => setIsVisible(!isVisible)}
            >
                {groupName}
                <div className="pc-group-name-data">
                    &nbsp; - &nbsp;
                    {groupState.currentActiveCards
                        ? `${groupState.currentActiveCards} / ${groupState.allCards} ${smallScreen ? "" : "trenutno prisutnih"}`
                        : smallScreen ? "/" : "nema prisutnih"}
                </div>
                <div className="pv-group-button-container">
                    <motion.div
                        className="pv-group-button"
                        animate={{ rotate: isVisible ? 0 : 180 }}
                        transition={{ duration: 0.19, ease: "easeInOut" }}
                        style={{ transformOrigin: "center" }}
                    >
                        <svg
                            // xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="40"
                            height="40"
                            style={{ display: "block" }}
                        >
                            <path
                                d="M7 10l5 5 5-5"
                                fill="none"
                                stroke={isDarkTheme ? "#e2e1e1ff" : "black"}
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </motion.div>
                </div>
            </div>
            <div className="view-card-grid">
                {employees.map((employee, index) => (
                    <EmployeeCard key={employee.user_id} employee={employee} index={index} />
                ))}
                {Array.from({ length: emptySlots }).map((_, id) => (
                    <div key={`empty-${id}`} className="pv-card-container" style={{ display: "flex" }} />
                ))}
            </div>
        </motion.div>
    )
}
